<template>
  <assistant-step :title="$t(`${step.id}.title`)">
    <gc-form v-model="formValid" @submit.prevent="onSubmit()">
      <gc-row v-if="showBusinessCustomerFields">
        <gc-col cols="12" sm="6">
          <form-field
            :form-data="formData"
            field-name="companyName"
            :step="step"
          ></form-field>
        </gc-col>
        <gc-col cols="12" sm="6">
          <form-field
            :form-data="formData"
            field-name="vatId"
            :step="step"
          ></form-field>
        </gc-col>
      </gc-row>
      <gc-row>
        <gc-col cols="12" sm="6">
          <form-field
            :form-data="formData"
            field-name="firstname"
            :step="step"
          ></form-field>
        </gc-col>
        <gc-col cols="12" sm="6">
          <form-field
            :form-data="formData"
            field-name="lastname"
            :step="step"
          ></form-field>
        </gc-col>
      </gc-row>
      <gc-row>
        <gc-col cols="12" sm="9">
          <form-field
            :form-data="formData"
            field-name="street"
            :step="step"
          ></form-field>
        </gc-col>
        <gc-col cols="12" sm="3">
          <form-field
            :form-data="formData"
            field-name="streetNumber"
            :step="step"
          ></form-field>
        </gc-col>
      </gc-row>
      <gc-row>
        <gc-col cols="12" sm="4">
          <form-field
            :form-data="formData"
            field-name="zipcode"
            :step="step"
          ></form-field>
        </gc-col>
        <gc-col cols="12" sm="8">
          <form-field
            :form-data="formData"
            field-name="city"
            :step="step"
          ></form-field>
        </gc-col>
      </gc-row>
      <template v-if="showBillingAddress">
        <gc-row>
          <gc-col cols="12" sm="6">
            <form-field
              :form-data="formData"
              field-name="checkBillingAddress"
              :hideLabel="true"
              :step="step"
            >
              <template #label>
                <span
                  @click.stop
                  v-html="
                    $t(`${step.id}.formFields.checkBillingAddress.headline`)
                  "
                />
              </template>
            </form-field>
          </gc-col>
        </gc-row>
        <template v-if="showBillingAddressFields">
          <gc-row>
            <gc-col cols="12" sm="6">
              <form-field
                :form-data="formData"
                field-name="firstnameBilling"
                :step="step"
              ></form-field>
            </gc-col>
            <gc-col cols="12" sm="6">
              <form-field
                :form-data="formData"
                field-name="lastnameBilling"
                :step="step"
              ></form-field>
            </gc-col>
          </gc-row>
          <gc-row>
            <gc-col cols="12" sm="9">
              <form-field
                :form-data="formData"
                field-name="streetBilling"
                :step="step"
              ></form-field>
            </gc-col>
            <gc-col cols="12" sm="3">
              <form-field
                :form-data="formData"
                field-name="streetNumberBilling"
                :step="step"
              ></form-field>
            </gc-col>
          </gc-row>
          <gc-row>
            <gc-col cols="12" sm="4">
              <form-field
                :form-data="formData"
                field-name="zipcodeBilling"
                :step="step"
              ></form-field>
            </gc-col>
            <gc-col cols="12" sm="8">
              <form-field
                :form-data="formData"
                field-name="cityBilling"
                :step="step"
              ></form-field>
            </gc-col>
          </gc-row>
        </template>
      </template>
      <gc-row>
        <gc-col>
          <form-field
            :form-data="formData"
            field-name="phone"
            :step="step"
          ></form-field>
        </gc-col>
      </gc-row>
      <gc-row>
        <gc-col>
          <form-field
            :form-data="formData"
            field-name="email"
            :step="step"
          ></form-field>
        </gc-col>
      </gc-row>
      <gc-row v-if="showCustomerNumberField">
        <gc-col>
          <form-field
            :form-data="formData"
            field-name="customerNumber"
            :step="step"
          ></form-field>
        </gc-col>
      </gc-row>
      <gc-row v-if="showNewsletterConfirmationField">
        <gc-col>
          <form-field
            :form-data="formData"
            field-name="newsletterConfirmation"
            :step="step"
          >
            <template #label>
              <span
                @click.stop
                v-html="
                  $t(`${step.id}.formFields.newsletterConfirmation.headline`)
                "
              />
            </template>
          </form-field>
        </gc-col>
      </gc-row>
      <gc-row>
        <gc-col>
          <form-field
            :hide-label="!showNewsletterConfirmationField"
            :form-data="formData"
            field-name="privacyConfirmation"
            :step="step"
          >
            <template #label>
              <span
                @click.stop
                v-html="
                  $t(`${step.id}.formFields.privacyConfirmation.headline`)
                "
              />
            </template>
          </form-field>
        </gc-col>
      </gc-row>
    </gc-form>

    <div class="gc-text-sm gc-text-gray-400" v-if="showRequiredHint">
      <span class="gc-text-primary">*</span> {{ $t('requiredHint') }}
    </div>

    <assistant-step-navigation
      :assistantContext="assistantContext"
      :step="step"
      :steps="steps"
      :submit="step.submit"
      :valid="formValid"
      :show-back-button="true"
      @next="onNext()"
      @prev="onPrev()"
    />
  </assistant-step>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from '@vue/composition-api';
import AssistantStep from '@/components/AssistantStep.vue';
import GcCol from '@/components/primitives/GcCol.vue';
import GcForm from '@/components/primitives/GcForm.vue';
import GcRow from '@/components/primitives/GcRow.vue';
import AssistantStepNavigation from '@/components/AssistantStepNavigation.vue';
import { getStepContext } from '@/lib/context';
import { stepComponentProps } from '@/lib/steps/helper';
import FormField from '@/components/FormField.vue';
import { useBaseTransitions } from '@/lib/xstate/transitions';
import { AssistantContext } from '~/steps/steps';

export default defineComponent({
  components: {
    AssistantStep,
    AssistantStepNavigation,
    FormField,
    GcCol,
    GcForm,
    GcRow,
  },
  props: {
    ...stepComponentProps,
  },
  setup: (props, { emit }) => {
    const { onNext, onPrev } = useBaseTransitions(
      emit,
      props.assistantContext,
      props.step,
    );
    const formData = getStepContext(props.assistantContext, props.step);
    const formValid = ref(false);

    const showBusinessCustomerFields = computed<boolean>(() =>
      props.step?.typeOptions?.contactForm?.isBusinessCustomer
        ? props.step.typeOptions.contactForm.isBusinessCustomer(
            props.assistantContext as AssistantContext,
          )
        : false,
    );

    const showCustomerNumberField = computed<boolean>(() =>
      props.step?.typeOptions?.contactForm?.showCustomerNumber
        ? props.step.typeOptions.contactForm.showCustomerNumber(
            props.assistantContext as AssistantContext,
          )
        : false,
    );

    const showNewsletterConfirmationField = computed<boolean>(() =>
      props.step?.typeOptions?.contactForm?.showNewsletterConfirmation
        ? props.step.typeOptions.contactForm.showNewsletterConfirmation(
            props.assistantContext as AssistantContext,
          )
        : false,
    );

    const showBillingAddress = computed<boolean>(() =>
      props.step?.typeOptions?.contactForm?.showBillingAddress
        ? props.step.typeOptions.contactForm.showBillingAddress(
            props.assistantContext as AssistantContext,
          )
        : false,
    );

    const showBillingAddressFields = computed<boolean>(
      () => showBillingAddress.value && formData.value.checkBillingAddress,
    );

    const showRequiredHint = computed<boolean>(
      (): boolean =>
        process.env.VUE_APP_SHOW_REQUIRED_HINT === 'true' &&
        Boolean(
          props.step?.fields &&
            props.step?.fields.some((field) => field.required === true),
        ),
    );

    return {
      formData,
      formValid,
      onNext,
      onPrev,
      showBusinessCustomerFields,
      showCustomerNumberField,
      showNewsletterConfirmationField,
      showBillingAddress,
      showBillingAddressFields,
      showRequiredHint,
    };
  },
});
</script>
